// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {
	ANIMATED_IMAGE_FORMATS,
	BANNER_ASPECT_RATIO_LABEL,
	BANNER_MINIMUM_SIZE_LABEL,
	IMAGE_MAX_SIZE_LABEL,
	PREMIUM_PRODUCT_NAME,
} from '@app/features/app/config/I18nDisplayConstants';
import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {isLimitToggleEnabled} from '@app/features/app/utils/LimitUtils';
import {AssetCropModal, AssetType} from '@app/features/expressions/components/modals/AssetCropModal';
import {openFilePicker} from '@app/features/messaging/utils/FilePickerUtils';
import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import {shouldShowPremiumFeatures} from '@app/features/premium/utils/PremiumUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/BannerUploader.module.css';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';
export type BannerMode = 'inherit' | 'custom' | 'unset';
interface BannerUploaderProps {
	hasBanner: boolean;
	onBannerChange: (base64: string) => void;
	onBannerClear: () => void;
	disabled?: boolean;
	disableModeSelection?: boolean;
	isPerGuildProfile: boolean;
	errorMessage?: string;
	bannerMode?: BannerMode;
	onBannerModeChange?: (mode: BannerMode) => void;
}
export const BannerUploader = observer(
	({
		hasBanner,
		onBannerChange,
		onBannerClear,
		disabled,
		disableModeSelection,
		isPerGuildProfile,
		errorMessage,
		bannerMode = 'inherit',
		onBannerModeChange,
	}: BannerUploaderProps) => {
		const {t} = useLingui();
		const hasBannerEntitlement = isLimitToggleEnabled(
			{feature_animated_banner: LimitResolver.resolve({key: 'feature_animated_banner', fallback: 0})},
			'feature_animated_banner',
		);
		const getBannerModeOptions = useCallback(
			() => [
				{
					value: 'inherit' as BannerMode,
					name: t`Use Global Profile`,
					desc: t`Show your global profile banner in this community`,
				},
				{
					value: 'custom' as BannerMode,
					name: t`Use Custom Image`,
					desc: t`Upload a custom banner for this community`,
					disabled: !hasBannerEntitlement,
				},
				{
					value: 'unset' as BannerMode,
					name: t`Don't Show`,
					desc: t`Show accent color only, ignoring your global profile`,
				},
			],
			[t, hasBannerEntitlement],
		);
		const showBannerPremiumUpsell = useCallback(() => {
			if (!shouldShowPremiumFeatures()) return;
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={t`Profile banners require ${PREMIUM_PRODUCT_NAME}`}
						description={
							<Trans>
								Profile banners are a premium feature. Get {PREMIUM_PRODUCT_NAME} to add a banner to your profile.
							</Trans>
						}
						primaryText={t`Get ${PREMIUM_PRODUCT_NAME}`}
						primaryVariant="primary"
						secondaryText={t`Cancel`}
						onPrimary={() => {
							window.setTimeout(() => {
								PremiumModalCommands.open();
							}, 0);
						}}
					/>
				)),
			);
		}, [t]);
		const handleBannerUpload = useCallback(async () => {
			if (!hasBannerEntitlement) {
				showBannerPremiumUpsell();
				return;
			}
			try {
				const [file] = await openFilePicker({accept: 'image/*'});
				if (!file) return;
				if (file.size > 10 * 1024 * 1024) {
					ToastCommands.createToast({
						type: 'error',
						children: t`Banner file is too large. Please choose a file smaller than ${IMAGE_MAX_SIZE_LABEL}.`,
					});
					return;
				}
				const base64 = await AvatarUtils.fileToBase64(file);
				ModalCommands.push(
					modal(() => (
						<AssetCropModal
							imageUrl={base64}
							sourceMimeType={file.type}
							assetType={AssetType.PROFILE_BANNER}
							onCropComplete={(croppedBlob) => {
								const reader = new FileReader();
								reader.onload = () => {
									const croppedBase64 = reader.result as string;
									onBannerChange(croppedBase64);
								};
								reader.onerror = () => {
									ToastCommands.createToast({
										type: 'error',
										children: t`Failed to process the cropped image. Please try again.`,
									});
								};
								reader.readAsDataURL(croppedBlob);
							}}
							onSkip={() => {
								onBannerChange(base64);
							}}
						/>
					)),
				);
			} catch {
				ToastCommands.createToast({
					type: 'error',
					children: t`That image is invalid. Please try another one.`,
				});
			}
		}, [hasBannerEntitlement, onBannerChange, showBannerPremiumUpsell, t]);
		const handleModeChange = useCallback(
			(mode: BannerMode) => {
				onBannerModeChange?.(mode);
				if (mode === 'custom') {
					handleBannerUpload();
				}
			},
			[onBannerModeChange, handleBannerUpload],
		);
		const bannerModeOptions = getBannerModeOptions();
		const radioGroupDisabled =
			disableModeSelection ?? Boolean(disabled && !(isPerGuildProfile && !hasBannerEntitlement));
		if (isPerGuildProfile && onBannerModeChange) {
			return (
				<div>
					<div className={styles.label}>
						<Trans>Banner</Trans>
					</div>
					<RadioGroup
						options={bannerModeOptions}
						value={bannerMode}
						disabled={radioGroupDisabled}
						onChange={handleModeChange}
						aria-label={t`Banner mode selection`}
					/>
					{bannerMode === 'custom' && (
						<div className={styles.buttonGroup}>
							<Button variant="primary" small={true} onClick={handleBannerUpload} disabled={disabled}>
								<Trans>Change Banner</Trans>
							</Button>
							{hasBanner && (
								<Button
									variant="secondary"
									small={true}
									onClick={() => {
										onBannerModeChange('inherit');
										onBannerClear();
									}}
									disabled={disabled}
								>
									<Trans>Remove Banner</Trans>
								</Button>
							)}
						</div>
					)}
					<div className={clsx(styles.description, styles.helperSpacing)}>
						<Trans>
							{ANIMATED_IMAGE_FORMATS}. Max {IMAGE_MAX_SIZE_LABEL}. Minimum: {BANNER_MINIMUM_SIZE_LABEL} (
							{BANNER_ASPECT_RATIO_LABEL})
						</Trans>
					</div>
					{errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
				</div>
			);
		}
		return (
			<div>
				<div className={styles.label}>
					<Trans>Banner</Trans>
				</div>
				<div className={styles.buttonGroup}>
					<Button variant="primary" small={true} onClick={handleBannerUpload} disabled={disabled}>
						<Trans>Change Banner</Trans>
					</Button>
					{hasBanner && (
						<Button variant="secondary" small={true} onClick={onBannerClear} disabled={disabled}>
							<Trans>Remove Banner</Trans>
						</Button>
					)}
				</div>
				<div className={styles.description}>
					<Trans>
						{ANIMATED_IMAGE_FORMATS}. Max {IMAGE_MAX_SIZE_LABEL}. Minimum: {BANNER_MINIMUM_SIZE_LABEL} (
						{BANNER_ASPECT_RATIO_LABEL})
					</Trans>
				</div>
				{!hasBannerEntitlement && (
					<div className={styles.description}>
						<Trans>Profile banners require {PREMIUM_PRODUCT_NAME}.</Trans>
					</div>
				)}
				{errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
			</div>
		);
	},
);
